import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_1_700, COLOR_PRIMARY_2 } from 'src/colors';
import DynamicBarChart from 'src/App/Components/Charts/DynamicBarChart';
import { BarGraphData } from 'src/interfaces/idashboard';
import { User } from 'src/interfaces/iuser';
import { getNestedValue, KPI_CONFIG, metricKeyNameMap } from './helper';
import { exchangeRates } from 'src/const';

// Dummy Data
const dummyData: BarGraphData[] = [
  { keyLong: 'New York', keyShort: 'New York', value: 1900 },
  { keyLong: 'Los Angeles', keyShort: 'Los Angeles', value: 950 },
  { keyLong: 'Chicago', keyShort: 'Chicago', value: 700 },
  { keyLong: 'Houston', keyShort: 'Houston', value: 650 },
  { keyLong: 'Phoenix', keyShort: 'Phoenix', value: 500 },
];

const KpiChart = ({
  data,
  currency,
  user,
  handleOnSampleClick,
  handleComparisonWrapper,
  comparisons,
  isMobileScreen,
  source,
  marketComparisons,
  selectedBedroom,
}: {
  data: any;
  currency: string;
  user: User;
  handleOnSampleClick: () => void;
  handleComparisonWrapper: () => void;
  comparisons: any;
  isMobileScreen: boolean;
  source: string;
  marketComparisons: any;
  selectedBedroom: string | number;
}) => {
  const [selectedKpi, setSelectedKpi] = useState<string>('occupancy_rate');

  const handleKpiChange = (event: SelectChangeEvent<string>) => {
    setSelectedKpi(event.target.value as string);
  };

  const isAnyBedroomSelected = selectedBedroom === 'any';
  const selectedKPIOptions = isAnyBedroomSelected ? 'ANY_BEDROOM' : 'BEDROOM_SPECIFIC';

  const chartDataObj: { data: BarGraphData[]; unit: string } = useMemo(() => {
    const chartData: BarGraphData[] = comparisons
      .map(comparison => {
        const marketLabel = comparison.label;
        const kpiPathKey = KPI_CONFIG[selectedKPIOptions][selectedKpi].path;
        const marketData: any = data[marketLabel]?.message;

        if (!marketData) return null;

        const kpiData = getNestedValue(
          marketData,
          kpiPathKey,
          isAnyBedroomSelected ? undefined : selectedBedroom.toString()
        );

        return {
          keyLong: marketLabel,
          keyShort: marketLabel,
          value: kpiData,
        };
      })
      .filter(Boolean);

    let unit = KPI_CONFIG[selectedKPIOptions][selectedKpi].symbol;
    if (unit === 'currency') {
      unit = exchangeRates[currency].symbol;
    }

    return { data: chartData, unit };
  }, [comparisons, data, selectedKpi, selectedBedroom]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: 2,
            gap: '10px',
            flexWrap: 'wrap',            
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={isMobileScreen ? {fontSize: '16px', marginBottom: '0px'} : {}}
          >
            Market Performance by
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: COLOR_PRIMARY_2,
                ...(isMobileScreen ? {fontSize: '16px'} : {})
              }}
            >
              <Select
                labelId="kpi-select-label"
                id="kpi-select"
                value={selectedKpi}
                variant="standard"
                onChange={handleKpiChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                }}
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  color: 'inherit',
                }}
              >
                {Object.keys(KPI_CONFIG[selectedKPIOptions]).map(kpi => (
                  <MenuItem key={kpi} value={kpi}>
                    {metricKeyNameMap[kpi]}
                  </MenuItem>
                ))}
              </Select>
            </Typography>
          </FormControl>
        </Box>
        <Box>
          <DynamicBarChart
            layout="horizontal"
            data={chartDataObj.data}
            unit={chartDataObj.unit}
            label=""
            height={isMobileScreen ? 200 : 150}
            barColor={COLOR_PRIMARY_1_700}
            barAccentColor={COLOR_PRIMARY_1}
            useShortHand={true}
            isPropertySizeGraph={true}
            getTooltipText={undefined}
            xAxisLabelOrientation={isMobileScreen ? "vertical" : "horizontal"}
            tickFormattorXAxis={(value) => {
              return value.toString().length > 7 ? value.toString().slice(0, 7) + '...' : value.toString();
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default KpiChart;
